import { $ApiModuleName, $ValueModuleName } from "@omni/types/utils/store";
import { canUseDOM } from "@omni/utils/dom.utils";
import { attempt } from "@omni/utils/function.utils";
import { Reducer } from "redux";

import { Config } from "@/utils/config.utils";
import { parseElementForCommentedContent } from "@/utils/ssr";
import { flattenObj } from "@/utils/translations";

import { API_STAGES } from "./create-api-module";
import { VALUE_STAGES } from "./create-value-module";
import { rebuildUrls } from "./modules/value/flyoutCategories/helpers/flyoutCategories.helpers";
import { IState, TStore, TValueAction } from "./store.types";
import { formatSingleContants } from "./utils";

type TComponentDataInfo = {
    key?: TValueStateKey | TApiStateKey;
    state?: Object;
    moduleName?: typeof $ValueModuleName | typeof $ApiModuleName;
};

const DataMappers: Partial<{
    [K in TValueStateKey]: (data: any) => IState["value"][K];
}> = {
    flyoutCategories: (data) => rebuildUrls(data, `/${Config.getLanguage()}/`),
    translations: (data) => ({ data: flattenObj(data.data), debug: data.debug })
};

type TValueStateKey = keyof IState["value"];
type TApiStateKey = keyof IState["api"]["data"];

const parseComponent = (component: HTMLElement): TComponentDataInfo => {
    return attempt<TComponentDataInfo>(() => {
        const key = component.dataset.ssrInitialdata as
            | TValueStateKey
            | TApiStateKey
            | undefined;

        const moduleName = component.dataset.ssrModuleName as
            | typeof $ValueModuleName
            | typeof $ApiModuleName
            | undefined;

        const json = parseElementForCommentedContent(component);

        return {
            key,
            state: json,
            // $ValueModuleName fallback for AEM generated initial states
            moduleName: moduleName || $ValueModuleName
        };
    }, {});
};

const getStageForModule = (
    moduleName: typeof $ValueModuleName | typeof $ApiModuleName
) => {
    if (moduleName === $ValueModuleName) {
        return VALUE_STAGES.Init;
    }

    return API_STAGES.Init;
};

const resolveInitialState = (store: TStore) => {
    if (canUseDOM()) {
        const elementNodeListOf: NodeList = document.querySelectorAll(
            "script[data-ssr-initialdata]"
        );
        const nodes: Node[] = Array.from(elementNodeListOf);
        nodes.forEach((node) => {
            const { key, state, moduleName } = parseComponent(
                node as HTMLElement
            );
            if (key && state && moduleName) {
                const type = formatSingleContants(
                    moduleName,
                    key,
                    getStageForModule(moduleName)
                );

                let mappedState = state;
                if (moduleName === $ValueModuleName) {
                    const valueKey = key as TValueStateKey;
                    mappedState = DataMappers[valueKey]?.(state) || state;
                }

                store.dispatch({
                    type,
                    payload: mappedState
                });
            }
        });
    }
};

const initializingReducer =
    <S, TStateType, TActionType extends TValueAction>(
        key: keyof TStateType
    ): Reducer<S | undefined, TActionType> =>
    (state: any = {}, action: TActionType) => {
        const { type, payload } = action;
        const matches = /(.*)\/(.*)\/(init)/.exec(type);

        if (!matches) return state;

        const [, prefix, namespace, valueStage] = matches;
        if (
            ![$ValueModuleName, $ApiModuleName].includes(prefix as any) ||
            key !== namespace
        ) {
            return state;
        }

        if (valueStage.toUpperCase() === VALUE_STAGES.Init) {
            return payload;
        }

        return state;
    };

export { initializingReducer, resolveInitialState };
